import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { decryptBinaryUseCase } from '@/domain/usecases/textEncryption/decryptBinaryUseCase';
import { initializeTextEncryptionUseCase } from '@/domain/usecases/textEncryption/initializeTextEncryptionUseCase';
import { resolveStorageDownloadUrlUseCase } from '@/domain/usecases/storage/resolveStorageDownloadUrlUseCase';

/**
 * EncryptedCachedImage — `CachedImage` sibling for E2EE-encrypted chat media.
 *
 * `CachedImage` renders a plaintext URL the browser can fetch+decode directly.
 * Chat image/thumbnail bytes are NOT plaintext: they are ciphertext produced
 * by `encryptBinary` (Task 9/10/11) sitting at a Firebase Storage object. This
 * component takes the bytes' BUCKET-RELATIVE object path as `src`, resolves a
 * valid (`%2F`-encoded, tokenized) Firebase Storage download URL via the domain
 * `ResolveStorageDownloadUrlUseCase`, then inserts a fetch →
 * `DecryptBinaryUseCase` → `Blob` → `blob:` URL hop before paint, and renders
 * the decrypted `blob:` URL in a plain `<img>`.
 *
 * Why a relative path, not a ready URL (Finding 5 / review_2 #3,#4): the old
 * contract handed a `DEFAULT_BUCKET_PREFIX + relativePath` concatenation, which
 * left the object segment's `/`s unencoded and carried no download token — the
 * Firebase Storage REST endpoint rejects that with HTTP 400. Resolving the URL
 * here through the UseCase (which fronts `getDownloadURL(ref(storage, …))`)
 * yields the same valid URL the upload side produces. The component reaches
 * storage ONLY through the domain UseCase — it does not import `firebase/storage`
 * or the data-layer `storage` singleton (Clean Architecture: dependencies point
 * into the domain).
 *
 * Web-first media-crypto design — no Flutter parity (story Assumption A /
 * consideration A/E). Legacy / Flutter-origin unencrypted media is accepted to
 * fail to decrypt and surfaces the error placeholder rather than a broken
 * image.
 *
 * Prop surface mirrors `CachedImage` (`src`, `alt`, `placeholder`,
 * `errorPlaceholder`, `onLoad`, `onError`, `style`, `className`, `qaId`) plus
 * `peerPublicJwk` — the peer's ECDH public JWK used to (re-)derive the chat
 * AES-GCM key before decrypting.
 *
 * Caching: decrypted `blob:` URLs are held in a module-level LRU cache keyed by
 * the relative object path (`src` — the component's stable input, unaffected by
 * the per-resolve download token), with `loadingPromises` dedupe so the same
 * ciphertext is never resolved + fetched + decrypted twice (mirrors
 * `CachedImage`'s `imageCache` / `loadingPromises` shape and eviction-policy
 * shape). The URL-resolve happens INSIDE the deduped `fetchAndDecrypt` promise,
 * so the dedupe covers resolve+fetch+decrypt as one unit. The cache OWNS the
 * `blob:` URLs and revokes them on LRU eviction — see the revocation note on
 * `cacheDecryptedBlob`.
 *
 * Single-key-slot caveat: `textEncryptionService` has one derived-key slot
 * shared across the text + binary paths. The chat already derives this key for
 * the open conversation's peer at load time (`useChat.runInitialLoad`). All
 * tiles in a single open chat share that ONE peer, so re-initializing here with
 * the same `peerPublicJwk` is idempotent (re-derives the identical key) and
 * cannot clobber another peer's decrypt — the component never interleaves two
 * different peers. The re-init is a defensive self-heal so a tile still decrypts
 * if the singleton key was changed by an unrelated flow after chat load.
 */

interface EncryptedCachedImageProps {
  /**
   * Bucket-RELATIVE object path of the ciphertext (the encrypted thumbnail /
   * full-image bytes), e.g. `chat/{group}/thumbnail/{ts}` — NOT a ready URL.
   * The component resolves a valid, tokenized Firebase Storage download URL from
   * this path via the domain UseCase before fetching (Finding 5). It also keys
   * the decrypted-blob LRU + in-flight dedupe by this path.
   */
  src: string;
  alt?: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  placeholder?: React.ReactNode;
  errorPlaceholder?: React.ReactNode;
  onLoad?: () => void;
  onError?: () => void;
  style?: React.CSSProperties;
  /**
   * Peer's ECDH public JWK. Used to (re-)derive the chat AES-GCM key before
   * decrypting so the decrypt uses the right peer key. When null/undefined the
   * service falls through to pass-through mode and the ciphertext fails to
   * render (error placeholder), matching the legacy-media contract.
   */
  peerPublicJwk?: JsonWebKey | null;
  /**
   * Optional QA element id. When provided, the rendered node carries
   * `data-qa-id` (from `qaId`) plus a `data-qa-status` reflecting the
   * fetch+decrypt lifecycle (`loading` | `ready` | `error`). The status is
   * owned by the load lifecycle, so this component intentionally narrows to a
   * bare id rather than the full `QaAttrOptions` — same shape as `CachedImage`.
   */
  qaId?: string;
}

interface CacheEntry {
  /** The decrypted `blob:` object URL. */
  blobUrl: string;
  lastAccessed: number;
}

// Configuration for cache management — same shape as `CachedImage`'s
// `CACHE_CONFIG` (entry-count LRU). Decrypted blobs are heavier than HTTP-cache
// metadata, so the entry ceiling is lower; the eviction policy shape is the
// same (drop the oldest `evictionPercentage` when above
// `maxEntries * cleanupThreshold`).
const CACHE_CONFIG = {
  maxEntries: 100,
  cleanupThreshold: 0.8,
  evictionPercentage: 0.2,
};

// In-memory cache of decrypted `blob:` URLs with LRU tracking, plus a
// concurrent-request dedupe map (mirrors `CachedImage`'s `imageCache` +
// `loadingPromises`).
const decryptedCache = new Map<string, CacheEntry>();
const loadingPromises = new Map<string, Promise<string>>();

/**
 * Update last-accessed time for an entry (LRU touch).
 */
const touchCacheEntry = (src: string): void => {
  const entry = decryptedCache.get(src);
  if (entry) {
    entry.lastAccessed = Date.now();
  }
};

/**
 * Evict the oldest `evictionPercentage` of entries and REVOKE their `blob:`
 * URLs. The cache owns the blob URLs (they are shared across every component
 * mounting the same `src`), so a component must NOT revoke a cached blob on
 * unmount — that would break other live tiles using the same entry. Revocation
 * happens here, on eviction, so the URL outlives every active reference while
 * the bounded cache still prevents an unbounded `blob:` leak.
 */
const evictOldestEntries = (
  percentageToEvict: number = CACHE_CONFIG.evictionPercentage,
): void => {
  const entriesToRemove = Math.floor(decryptedCache.size * percentageToEvict);
  if (entriesToRemove === 0) return;

  const sortedEntries = Array.from(decryptedCache.entries()).sort(
    (a, b) => a[1].lastAccessed - b[1].lastAccessed,
  );

  for (let i = 0; i < entriesToRemove; i++) {
    const [src, entry] = sortedEntries[i];
    decryptedCache.delete(src);
    URL.revokeObjectURL(entry.blobUrl);
  }
};

/**
 * Insert a freshly-decrypted `blob:` URL into the cache and run LRU cleanup if
 * the cache is over capacity.
 */
const cacheDecryptedBlob = (src: string, blobUrl: string): void => {
  decryptedCache.set(src, { blobUrl, lastAccessed: Date.now() });

  if (decryptedCache.size >= CACHE_CONFIG.maxEntries * CACHE_CONFIG.cleanupThreshold) {
    evictOldestEntries();
  }
};

/**
 * Resolve the download URL for the bucket-relative `relativePath`, fetch the
 * ciphertext, derive the chat key for `peerPublicJwk`, decrypt to plaintext
 * bytes, wrap in a `Blob`, and return a `blob:` object URL. Throws on any
 * resolve / fetch / decrypt failure so the caller renders the error placeholder
 * (consideration A/E — legacy/unencrypted media is accepted to fail).
 *
 * The download-URL resolve runs HERE (inside the deduped promise) so the
 * in-flight dedupe + LRU — both keyed by `relativePath` — cover resolve, fetch,
 * and decrypt as a single unit. The relative path is the stable cache key; the
 * resolved download URL carries a per-resolve token and is not reused as a key.
 */
const fetchAndDecrypt = async (
  relativePath: string,
  peerPublicJwk: JsonWebKey | null | undefined,
): Promise<string> => {
  // (Re-)derive the chat AES-GCM key for this peer. Idempotent for the single
  // open-chat peer (see component JSDoc); a no-op-cheap call when the chat
  // already initialized the same key at load time.
  await initializeTextEncryptionUseCase.execute(peerPublicJwk ?? null);

  // Resolve a valid (%2F-encoded, tokenized) Firebase Storage download URL via
  // the domain UseCase. Concatenating `DEFAULT_BUCKET_PREFIX + relativePath`
  // (the old contract) produced an un-encoded, token-less URL the Storage REST
  // endpoint rejected with HTTP 400 (Finding 5).
  const downloadUrl = await resolveStorageDownloadUrlUseCase.execute(relativePath);
  if (!downloadUrl) {
    throw new Error(
      `Failed to resolve encrypted-media download URL: ${relativePath}`,
    );
  }

  const response = await fetch(downloadUrl);
  if (!response.ok) {
    throw new Error(
      `Failed to fetch encrypted media (${response.status}): ${relativePath}`,
    );
  }

  const ciphertext = await response.arrayBuffer();
  const plaintext = await decryptBinaryUseCase.execute(ciphertext);
  const blob = new Blob([plaintext]);
  return URL.createObjectURL(blob);
};

/**
 * EncryptedCachedImage — fetch → decrypt → `blob:` render for E2EE chat media.
 */
export const EncryptedCachedImage: React.FC<EncryptedCachedImageProps> = ({
  src,
  alt = '',
  className = '',
  loading = 'lazy',
  placeholder,
  errorPlaceholder,
  onLoad,
  onError,
  style,
  peerPublicJwk,
  qaId,
}) => {
  const { theme } = useTheme();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const isMountedRef = useRef(true);

  // Default placeholder using the theme secondary color — same surface as
  // `CachedImage`'s default placeholder.
  const defaultPlaceholder = (
    <div
      className={className}
      style={{
        ...style,
        backgroundColor: theme.secondary,
      }}
    />
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!src) {
      setIsLoading(false);
      setHasError(true);
      setBlobUrl(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setHasError(false);

    // 1) Memory-cache hit — reuse the decrypted `blob:` URL immediately.
    const cached = decryptedCache.get(src);
    if (cached) {
      touchCacheEntry(src);
      setBlobUrl(cached.blobUrl);
      setIsLoading(false);
      setHasError(false);
      onLoad?.();
      return;
    }

    // 2) In-flight dedupe — another tile is already fetching+decrypting this
    //    exact ciphertext; await its promise instead of doing the work twice.
    let promise = loadingPromises.get(src);
    if (!promise) {
      promise = fetchAndDecrypt(src, peerPublicJwk)
        .then((url) => {
          // The promise resolver caches the result so subsequent mounts hit
          // the cache. Revocation is owned by the cache's LRU eviction.
          cacheDecryptedBlob(src, url);
          loadingPromises.delete(src);
          return url;
        })
        .catch((error: unknown) => {
          loadingPromises.delete(src);
          throw error;
        });
      loadingPromises.set(src, promise);
    }

    promise
      .then((url) => {
        if (cancelled || !isMountedRef.current) return;
        setBlobUrl(url);
        setIsLoading(false);
        setHasError(false);
        onLoad?.();
      })
      .catch((error: unknown) => {
        appLogger.error(
          'Failed to fetch/decrypt encrypted media',
          'EncryptedCachedImage',
          { error: error as Error, context: { src } },
        );
        if (cancelled || !isMountedRef.current) return;
        setBlobUrl(null);
        setIsLoading(false);
        setHasError(true);
        onError?.();
      });

    // On `src` change / unmount, drop this render's interest in the in-flight
    // promise (state updates are guarded by `cancelled`). The decrypted
    // `blob:` URL itself is owned by the cache and revoked on LRU eviction, so
    // it is intentionally NOT revoked here — other live tiles may share it.
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, peerPublicJwk]);

  // QA props (id + load-state status) only when a `qaId` is supplied. Same
  // (`loading` | `ready` | `error`) token set as `CachedImage`.
  const qaStatus = hasError ? 'error' : isLoading || !blobUrl ? 'loading' : 'ready';
  const qaProps = qaId ? qaAttr({ id: qaId, status: qaStatus }) : {};

  if (hasError) {
    return (
      <>
        {errorPlaceholder || (
          <div
            {...qaProps}
            className={`${className} flex items-center justify-center`}
            style={{
              ...style,
              backgroundColor: theme.secondary,
            }}
          />
        )}
      </>
    );
  }

  if (isLoading || !blobUrl) {
    if (qaId) {
      // Wrap the placeholder so the QA status token is locatable while loading.
      return (
        <div {...qaProps} className={className} style={style}>
          {placeholder || defaultPlaceholder}
        </div>
      );
    }
    return <>{placeholder || defaultPlaceholder}</>;
  }

  return (
    <img
      {...qaProps}
      src={blobUrl}
      alt={alt}
      className={className}
      loading={loading}
      style={style}
      onError={() => {
        // The bytes decrypted successfully but did not decode as an image
        // (e.g. corrupt / non-image plaintext). Surface the error placeholder
        // and fire `onError` so the QA status flips to `error`.
        if (!isMountedRef.current) return;
        appLogger.error(
          'Decrypted media failed to decode as image',
          'EncryptedCachedImage',
          { context: { src } },
        );
        setHasError(true);
        onError?.();
      }}
    />
  );
};
